import { IOption } from '@sgi/gravity';
import React, { useEffect, useState } from 'react';
import { FormSelect } from 'src/components/forms/components/FormSelect';
import { Country, CountryLabel, Provinces, ProvincesLabel, States, StatesLabel } from '../../types/Location';
import { useFormContext } from 'react-hook-form';

interface Location {
  country: string;
  jurisdiction: string;
}

interface Props {
  defaultLocation: Location;
  isCountryVisible?: boolean;
  isJurisdictionVisible?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  errorOfCountry?: string;
  errorOfJurisdiction?: string;
  width?: string;
  handleInputChange?: (payload: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const LocationSelect = ({
  defaultLocation,
  isCountryVisible = true,
  isJurisdictionVisible = true,
  placeholder,
  isRequired = false,
  isReadOnly = false,
  handleInputChange,
}: Props) => {
  const { setValue, watch } = useFormContext<Location>();

  const countryList = (): IOption[] => {
    const countryList: IOption<string>[] = (Object.keys(Country) as (keyof typeof Country)[])
      .map(
        (value, key) =>
          ({
            label: CountryLabel[value],
            key: key,
            value: value,
          }) as unknown as IOption<string>,
      )
      .sort((a, b) => {
        if (a.value && b.value && a.value.localeCompare(b.value)) return 1;
        return -1;
      });
    return countryList;
  };

  const stateList = (): IOption[] => {
    return (Object.keys(States) as (keyof typeof States)[])
      .map(
        (value, key) =>
          ({
            label: StatesLabel[value],
            key: key,
            value: value,
          }) as unknown as IOption<string>,
      )
      .sort((a, b) => {
        if (a.value && b.value && a.value.localeCompare(b.value)) return 1;
        return -1;
      });
  };

  const provinceList = (): IOption[] => {
    const provinces = (Object.keys(Provinces) as (keyof typeof Provinces)[])
      .map(
        (value, key) =>
          ({
            label: ProvincesLabel[value],
            key: key,
            value: value,
          }) as unknown as IOption<string>,
      )
      .sort((a, b) => {
        if (a.value && b.value && a.value.localeCompare(b.value)) return 1;
        return -1;
      });
    // if (options.includes('IgnoreSaskatchewan')) {
    //   provinces = provinces.filter((j) => j.value !== Provinces.Saskatchewan);
    // }
    return provinces;
  };

  const [countryItems] = useState(countryList);

  const [jurisdictionItems, setJurisdictionItems] = useState<IOption[]>([]);

  const getJurisdiction = (country: Country | string) => {
    let jurisdictions: IOption[] = [];
    if (country === Country.Canada) {
      jurisdictions = provinceList();
      setJurisdictionItems(provinceList);
    } else if (country === Country.UnitedStates) {
      jurisdictions = stateList();
    } else {
      jurisdictions = [
        {
          label: 'Not available for International addresses',
          key: 'N/A',
          value: undefined,
        } as unknown as IOption<string>,
      ];
    }

    return jurisdictions;
  };

  useEffect(() => {
    const jurisdictions = getJurisdiction(defaultLocation.country);
    setJurisdictionItems(jurisdictions);
  }, []);

  const onChangeLocation = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.name === 'country') {
      const jurisdictions = getJurisdiction(e.target.value);
      setJurisdictionItems(jurisdictions);
      setValue('jurisdiction', '');
    }
    handleInputChange?.(e);
  };

  return (
    <>
      {isCountryVisible && (
        <FormSelect
          name="country"
          label="Country"
          isReadOnly={isReadOnly}
          isDisabled={isReadOnly}
          data-testid={'test-country'}
          isRequired={isRequired}
          placeholder={placeholder ?? 'Select Country'}
          onChange={onChangeLocation}
          controlStyleProps={{ maxWidth: { base: '100%', sm: '320px' } }}
        >
          {Object.entries(countryItems).map(([key, value]) => (
            <option key={key} value={value.value}>
              {value.label}
            </option>
          ))}
        </FormSelect>
      )}
      {watch('country').trim() !== '' && isJurisdictionVisible && (
        <FormSelect
          name="jurisdiction"
          label="Province/ State/ Region"
          isReadOnly={isReadOnly}
          isDisabled={isReadOnly}
          data-testid={'test-jurisdiction'}
          isRequired={isRequired}
          placeholder="Select Province/ State/ Region"
          onChange={onChangeLocation}
          controlStyleProps={{ maxWidth: { base: '100%', sm: '320px' } }}
        >
          {Object.entries(jurisdictionItems).map(([key, value]) => (
            <option key={key} value={value.value}>
              {value.label}
            </option>
          ))}
        </FormSelect>
      )}
    </>
  );
};
